/**
 * Query Templates Management Page
 * Allows editing NetApp GraphQL query templates with backup and rollback functionality
 */

import React, { useState, useEffect } from 'react';
import {
  FileCode,
  Save,
  RotateCcw,
  RefreshCw,
  Clock,
  AlertTriangle,
  CheckCircle,
  Code2,
  History,
  ChevronDown,
  ChevronUp,
  Copy,
  Settings,
  Play,
  X,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  netappTemplatesAPI,
  QueryTemplate,
  TemplateBackup,
} from '../shared/api/netappTemplates';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumb } from '@/components/ui/Breadcrumb';

const QueryTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<QueryTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<QueryTemplate | null>(null);
  const [editedContent, setEditedContent] = useState<string>('');
  const [backups, setBackups] = useState<TemplateBackup[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [showBackups, setShowBackups] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [showTestModal, setShowTestModal] = useState(false);

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await netappTemplatesAPI.getTemplates();
      setTemplates(data);
      if (!selectedTemplate && data.length > 0) {
        selectTemplate(data[0]);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      setMessage({ type: 'error', text: 'Failed to load templates' });
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = async (template: QueryTemplate) => {
    if (isDirty) {
      const confirm = window.confirm('You have unsaved changes. Do you want to discard them?');
      if (!confirm) return;
    }

    setSelectedTemplate(template);
    setEditedContent(template.query_template);
    setIsDirty(false);
    setShowBackups(false);
    setBackups([]);

    // Load backups for this template
    try {
      const backupData = await netappTemplatesAPI.getBackups(template.task_name);
      setBackups(backupData);
    } catch (error) {
      console.error('Failed to load backups:', error);
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;

    setSaving(true);
    try {
      const response = await netappTemplatesAPI.updateTemplate(
        selectedTemplate.task_name,
        editedContent
      );
      setMessage({ type: 'success', text: response.message });
      setIsDirty(false);

      // Reload templates and backups
      await loadTemplates();
      const backupData = await netappTemplatesAPI.getBackups(selectedTemplate.task_name);
      setBackups(backupData);
    } catch (error) {
      console.error('Failed to save template:', error);
      setMessage({ type: 'error', text: 'Failed to save template' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestQuery = async () => {
    if (!selectedTemplate) return;

    setIsTesting(true);
    setTestResult(null);
    try {
      const result = await netappTemplatesAPI.testQuery(selectedTemplate.task_name);
      setTestResult(result);
      setShowTestModal(true);
    } catch (error) {
      console.error('Failed to test query:', error);
      setMessage({ type: 'error', text: 'Failed to test query' });
    } finally {
      setIsTesting(false);
    }
  };

  const handleRestore = async (backup: TemplateBackup) => {
    if (!selectedTemplate) return;

    const confirm = window.confirm(
      `Restore ${selectedTemplate.task_name} from backup created on ${backup.timestamp}?`
    );
    if (!confirm) return;

    setLoading(true);
    try {
      const response = await netappTemplatesAPI.restoreFromBackup(
        selectedTemplate.task_name,
        backup.filename
      );
      setMessage({ type: 'success', text: response.message });
      setEditedContent(response.query_template);
      setIsDirty(false);

      // Reload templates
      await loadTemplates();
    } catch (error) {
      console.error('Failed to restore backup:', error);
      setMessage({ type: 'error', text: 'Failed to restore backup' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBackup = async (backup: any) => {
    const confirm = window.confirm(`Delete backup "${backup.filename}"? This action cannot be undone.`);
    if (!confirm) return;

    setLoading(true);
    try {
      await netappTemplatesAPI.deleteBackup(selectedTemplate!.task_name, backup.filename);
      setMessage({ type: 'success', text: 'Backup deleted successfully' });
      // Reload backups
      const updatedBackups = await netappTemplatesAPI.getBackups(selectedTemplate!.task_name);
      setBackups(updatedBackups);
    } catch (error) {
      console.error('Failed to delete backup:', error);
      setMessage({ type: 'error', text: 'Failed to delete backup' });
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    const confirm = window.confirm('Sync all templates from files to database?');
    if (!confirm) return;

    setLoading(true);
    try {
      const response = await netappTemplatesAPI.syncTemplates(false);
      setMessage({ type: 'success', text: response.message });
      await loadTemplates();
    } catch (error) {
      console.error('Failed to sync templates:', error);
      setMessage({ type: 'error', text: 'Failed to sync templates' });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'info', text: 'Copied to clipboard!' });
    setTimeout(() => setMessage(null), 2000);
  };

  const formatTaskName = (name: string) => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: 'Settings', href: '/settings', icon: <Settings className="w-4 h-4" /> },
            { label: 'Query Templates', icon: <FileCode className="w-4 h-4" /> }
          ]}
        />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Query Templates Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Edit and manage NetApp GraphQL query templates with version control
            </p>
          </div>

          <button
            onClick={handleSync}
            disabled={loading}
            className="px-4 py-2 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white rounded-lg flex items-center gap-2 hover:shadow-lg transition-all duration-200"
          >
            <RefreshCw className="w-5 h-5" />
            Sync from Files
          </button>
        </div>

        {/* Alert Messages */}
        {message && (
          <div
            className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
              message.type === 'success'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : message.type === 'error'
                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : message.type === 'error' ? (
              <AlertTriangle className="w-5 h-5" />
            ) : (
              <Copy className="w-5 h-5" />
            )}
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Template List */}
          <div className="col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                  Templates
                </h2>
              </div>
              <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                {templates.map((template) => (
                  <button
                    key={template.task_name}
                    onClick={() => selectTemplate(template)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700 ${
                      selectedTemplate?.task_name === template.task_name
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-600'
                        : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {formatTaskName(template.task_name)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {template.task_name}
                        </div>
                      </div>
                      <Code2 className="w-4 h-4 text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Editor and Details */}
          <div className="col-span-9">
            {selectedTemplate ? (
              <div className="space-y-6">
                {/* Template Info */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                        {formatTaskName(selectedTemplate.task_name)}
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {selectedTemplate.description}
                      </p>
                      {selectedTemplate.updated_at && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                          Last updated: {format(new Date(selectedTemplate.updated_at), 'PPpp')}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isDirty && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 text-xs rounded">
                          Unsaved changes
                        </span>
                      )}
                      <button
                        onClick={() => setShowBackups(!showBackups)}
                        className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-2 text-sm"
                      >
                        <History className="w-4 h-4" />
                        Backups ({backups.length})
                        {showBackups ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleTestQuery()}
                        disabled={isTesting}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        <Play className="w-4 h-4" />
                        {isTesting ? 'Testing...' : 'Test Query'}
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={!isDirty || saving}
                        className={`px-4 py-1.5 rounded-lg transition-colors flex items-center gap-2 text-sm ${
                          isDirty
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <Save className="w-4 h-4" />
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Backups Panel */}
                {showBackups && backups.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                      Available Backups
                    </h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {backups.map((backup) => (
                        <div
                          key={backup.filename}
                          className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {backup.timestamp.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleRestore(backup)}
                              className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
                            >
                              <RotateCcw className="w-3 h-3" />
                              Restore
                            </button>
                            <button
                              onClick={() => handleDeleteBackup(backup)}
                              className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors flex items-center gap-1"
                            >
                              <Trash2 className="w-3 h-3" />
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Query Editor */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                        GraphQL Query Template
                      </h3>
                      <button
                        onClick={() => copyToClipboard(editedContent)}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <textarea
                      value={editedContent}
                      onChange={(e) => {
                        setEditedContent(e.target.value);
                        setIsDirty(e.target.value !== selectedTemplate.query_template);
                      }}
                      className="w-full h-96 p-4 font-mono text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      spellCheck={false}
                    />
                  </div>
                </div>

                {/* Info Panel */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div className="text-sm text-blue-800 dark:text-blue-300">
                      <p className="font-medium mb-2">Environment Mode: Development</p>
                      <ul className="space-y-1 text-xs list-disc list-inside">
                        <li>Changes will update both files and database</li>
                        <li>Backups are created automatically before each save</li>
                        <li>You can restore from any previous backup</li>
                        <li>Sync from files will reload templates from .graphql files</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
                <Code2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  Select a template to view and edit
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Test Query Result Modal */}
        {showTestModal && testResult && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-[#000742] rounded-xl shadow-2xl p-6 max-w-3xl w-full max-h-[80vh] overflow-auto border border-gray-200 dark:border-[#5869ff]/20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Query Test Results
                </h3>
                <button
                  onClick={() => {
                    setShowTestModal(false);
                    setTestResult(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Status */}
                <div className={`p-4 rounded-lg flex items-center gap-3 ${
                  testResult.success
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                    : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
                }`}>
                  {testResult.success ? (
                    <CheckCircle className="w-6 h-6" />
                  ) : (
                    <AlertTriangle className="w-6 h-6" />
                  )}
                  <div>
                    <div className="font-semibold">
                      {testResult.success ? 'Query Executed Successfully' : 'Query Execution Failed'}
                    </div>
                    <div className="text-sm mt-1">
                      {testResult.message || testResult.error}
                    </div>
                  </div>
                </div>

                {/* Customer Info */}
                {testResult.customer_id && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Customer ID</div>
                    <div className="text-sm text-gray-900 dark:text-white mt-1">{testResult.customer_id}</div>
                  </div>
                )}

                {/* Record Count */}
                {testResult.success && testResult.record_count !== undefined && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-sm font-medium text-blue-700 dark:text-blue-300">Records Retrieved</div>
                    <div className="text-2xl font-bold text-blue-900 dark:text-blue-200 mt-1">
                      {testResult.record_count}
                    </div>
                  </div>
                )}

                {/* Error Details */}
                {!testResult.success && testResult.details && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Error Details</div>
                    <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-auto">
                      {typeof testResult.details === 'string'
                        ? testResult.details
                        : JSON.stringify(testResult.details, null, 2)
                      }
                    </pre>
                  </div>
                )}

                {/* Raw Response */}
                {testResult.success && testResult.data && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Raw Response</div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(JSON.stringify(testResult.data, null, 2));
                        }}
                        className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                      >
                        Copy JSON
                      </button>
                    </div>
                    <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-auto max-h-96 p-3 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                      {JSON.stringify(testResult.data, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Sample Data (First 3 Records) */}
                {testResult.success && testResult.sample_data && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sample Data (First {Math.min(3, testResult.record_count || 0)} records)
                    </div>
                    <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-auto max-h-64 p-3 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                      {JSON.stringify(testResult.sample_data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    setShowTestModal(false);
                    setTestResult(null);
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default QueryTemplates;